import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import publicOutreachService, {
  ImportSource,
  PublicCompanyTarget,
  PublicOutreachCampaign,
} from '../services/publicOutreachService';
import { PageHeader, useConfirm, RelatedLinks, type RelatedLinkItem } from '../components/ui';
import { gtmPfad } from '../vertrieb/gtmLinks';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { rollenAufzaehlung, rollenMitRecht } from '../verwaltung/rollen';

const TARGET_STATUS: Record<string, string> = {
  collected: 'Gefunden',
  qualified: 'Adresse erfasst',
  pushed: 'Übergeben (Dry-Run)',
  sent: 'Versendet',
  replied: 'Antwort',
  bounced: 'Unzustellbar',
  suppressed: 'Gesperrt',
};

const PublicOutreachDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  // Welle 2: Recherche, Import, Sammeln, Versand und Opt-out sind Schreibaufrufe
  // im Bereich Versand (versand.ausfuehren). Lesende sehen Firmen und Export.
  const { darf } = useBerechtigung();
  const darfAusfuehren = darf('versand.ausfuehren');

  const [campaign, setCampaign] = useState<PublicOutreachCampaign | null>(null);
  const [targets, setTargets] = useState<PublicCompanyTarget[]>([]);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState<string>('');
  const [importSource, setImportSource] = useState<ImportSource>('contactlead');
  const [lemlistCampaignId, setLemlistCampaignId] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const c = await publicOutreachService.getCampaign(id);
      setCampaign(c);
      setTargets(c.targets || (await publicOutreachService.getTargets(id)));
      if (c.lemlistCampaignId) setLemlistCampaignId(c.lemlistCampaignId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kampagne konnte nicht geladen werden');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (label: string, fn: () => Promise<string>) => {
    setBusy(label);
    setError('');
    setInfo('');
    try {
      setInfo(await fn());
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || `${label} fehlgeschlagen`);
    } finally {
      setBusy('');
    }
  };

  const doResearch = () =>
    run('research', async () => {
      const r = await publicOutreachService.research(id!);
      return `KI-Recherche abgeschlossen: ${r.targetsCreated} Firmen hinzugefügt.`;
    });

  const doImport = () =>
    run('import', async () => {
      const r = await publicOutreachService.importSource(id!, importSource);
      return `${r.imported} Firmen aus Quelle "${importSource}" importiert.`;
    });

  const doCollect = () =>
    run('collect', async () => {
      const r = await publicOutreachService.collect(id!);
      return `Adressen gesammelt: ${r.found} gefunden, ${r.suppressed} gesperrt, ${r.failed} ohne Treffer (von ${r.scanned}).`;
    });

  const doPush = async () => {
    if (!lemlistCampaignId) {
      const ok = await confirm({
        title: 'Neue Lemlist-Kampagne anlegen?',
        message: 'Es ist keine Lemlist-Kampagnen-ID angegeben; die gesammelten Adressen werden in eine neu angelegte Lemlist-Kampagne übergeben. Rückgängig nur durch Löschen der Kampagne in Lemlist.',
        confirmLabel: 'Anlegen und übergeben',
      });
      if (!ok) return;
    }
    await run('push', async () => {
      const r = await publicOutreachService.push(id!, {
        lemlistCampaignId: lemlistCampaignId || undefined,
        createCampaign: !lemlistCampaignId,
      });
      return `${r.dryRun ? '[DRY-RUN] ' : ''}Versand: ${r.pushed} übergeben, ${r.skipped} übersprungen, ${r.failed} Fehler.`;
    });
  };

  const exportCsv = async () => {
    try {
      const blob = await publicOutreachService.exportTargets(id!, 'csv');
      publicOutreachService.downloadCSV(blob, `firmen-mailing-${id}.csv`);
    } catch {
      setError('Export fehlgeschlagen');
    }
  };

  const toggleOptOut = async (t: PublicCompanyTarget) => {
    try {
      await publicOutreachService.updateTarget(t.id, { optOut: !t.optOut });
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Aktualisierung fehlgeschlagen');
    }
  };

  if (!campaign) {
    return (
      <div className="page-container">
        {error ? <div className="alert alert-error">{error}</div> : <div className="loading-text">Lade…</div>}
      </div>
    );
  }

  const withEmail = targets.filter((t) => t.publicEmail).length;

  return (
    <div className="page-container">
      <PageHeader
        eyebrow={(
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/public-outreach')}>
            ← Zurück
          </button>
        )}
        title={campaign.name}
        subtitle={(
          <>
            Status: <span className="badge badge-info">{campaign.status}</span>
          </>
        )}
      />

      {(() => {
        // Grundlage und Ergebnis der Kampagne anspringbar machen.
        const items: RelatedLinkItem[] = [
          ...(campaign.icpId ? [{ key: 'icp', label: 'Zielkundenprofil', icon: 'target' as const, to: gtmPfad('icp', campaign.icpId), title: 'Zielkundenprofil im GTM-Setup öffnen' }] : []),
          ...(campaign.lemlistCampaignId ? [{ key: 'leads', label: 'Leads der Lemlist-Kampagne', icon: 'users' as const, to: `/lead-sync?kampagne=${encodeURIComponent(campaign.lemlistCampaignId)}`, title: 'Leads in Leads & Outreach' }] : []),
        ];
        return items.length > 0 ? <RelatedLinks className="mb-4" items={items} label="Verknüpft mit diesem Firmen-Mailing" /> : null;
      })()}

      {campaign.legalBasisNote && (
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <strong>Rechtsgrundlage:</strong> {campaign.legalBasisNote}
        </div>
      )}
      {error && <div className="alert alert-error">{error}</div>}
      {info && <div className="alert alert-success">{info}</div>}

      {/* Phasen */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 className="card-title">Workflow</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
          {darfAusfuehren && (
          <>
          <button className="btn btn-info" disabled={!!busy} onClick={doResearch}>
            {busy === 'research' ? 'Recherchiert…' : '1a · KI-Recherche (Claude)'}
          </button>

          <span style={{ display: 'inline-flex', gap: '0.25rem', alignItems: 'center' }}>
            <select className="form-input" style={{ width: 'auto' }} value={importSource}
              onChange={(e) => setImportSource(e.target.value as ImportSource)}>
              <option value="contactlead">Vorhandene Kontakte (ContactLead)</option>
              <option value="ausschreibungsmatcher">Ausschreibungsmatcher</option>
              <option value="visitor-reveal">Website-Besucher (Visitor-Reveal)</option>
            </select>
            <button className="btn btn-info" disabled={!!busy} onClick={doImport}>
              {busy === 'import' ? 'Importiert…' : '1b · Importieren'}
            </button>
          </span>

          <button className="btn btn-primary" disabled={!!busy} onClick={doCollect}>
            {busy === 'collect' ? 'Sammelt…' : '2 · Öffentliche Adressen sammeln'}
          </button>

          <span style={{ display: 'inline-flex', gap: '0.25rem', alignItems: 'center' }}>
            <input className="form-input" style={{ width: '12rem' }} placeholder="Lemlist-Kampagnen-ID (optional)"
              value={lemlistCampaignId} onChange={(e) => setLemlistCampaignId(e.target.value)} />
            <button className="btn btn-success" disabled={!!busy || withEmail === 0} onClick={doPush}>
              {busy === 'push' ? 'Versendet…' : '3 · Über Lemlist versenden'}
            </button>
          </span>
          </>
          )}

          <button className="btn btn-secondary" onClick={exportCsv}>CSV-Export</button>
        </div>
        <p className="text-xs text-muted" style={{ marginTop: '0.75rem' }}>
          {targets.length} Firmen · {withEmail} mit öffentlicher Adresse · Versand nur an öffentliche
          Sammeladressen mit Pflicht-Abmeldelink.
        </p>
        {!darfAusfuehren && (
          <p className="text-xs text-muted" data-testid="rechte-hinweis">
            Recherchieren, Sammeln, Versenden und Sperren sind {rollenAufzaehlung(rollenMitRecht('versand.ausfuehren'))} vorbehalten.
          </p>
        )}
      </div>

      {/* Targets */}
      {targets.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-description">
            {darfAusfuehren ? 'Noch keine Firmen. Starten Sie mit der KI-Recherche oder einem Import.' : 'Noch keine Firmen.'}
          </p>
        </div>
      ) : (
        <div className="card">
          <h3 className="card-title">Firmen &amp; Adressen</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Firma</th>
                  <th>Domain</th>
                  <th>Öffentliche Adresse</th>
                  <th>Quelle</th>
                  <th>Status</th>
                  <th>Opt-out</th>
                </tr>
              </thead>
              <tbody>
                {targets.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <Link to={`/companies?suche=${encodeURIComponent(t.domain || t.companyName)}`} title="Firma im CRM suchen">{t.companyName}</Link>
                    </td>
                    <td>{t.domain ? <a href={`https://${t.domain}`} target="_blank" rel="noopener noreferrer">{t.domain}</a> : '—'}</td>
                    <td>
                      {t.publicEmail ? (
                        <span title={t.sourceUrl || ''}>{t.publicEmail}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-xs">{t.source}</td>
                    <td>{TARGET_STATUS[t.status] || t.status}</td>
                    <td>
                      {darfAusfuehren ? (
                        <button className={`btn btn-sm ${t.optOut ? 'btn-danger' : 'btn-secondary'}`} onClick={() => toggleOptOut(t)}>
                          {t.optOut ? 'Gesperrt' : 'Sperren'}
                        </button>
                      ) : (
                        <span className={t.optOut ? 'badge badge-error' : 'text-muted'}>{t.optOut ? 'Gesperrt' : '—'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicOutreachDetailPage;
